import { Link } from "@tanstack/react-router";
import { Heart, Repeat2, Star, ShoppingCart, BadgeCheck } from "lucide-react";
import { ProductImage } from "./ProductImage";
import { effectivePrice, formatPrice, standardDisplayPrice, stockLabel, type Product } from "@/lib/shop";

export function ProductCard({
  product,
  onAdd,
  wishlisted,
  onWishlist,
  compared,
  onCompare,
}: {
  product: Product;
  onAdd: (p: Product) => void;
  wishlisted?: boolean;
  onWishlist?: (id: string) => void;
  compared?: boolean;
  onCompare?: (id: string) => void;
}) {
  const stock = stockLabel(product);
  const physio = product.physio_purchase_enabled && product.physio_price != null ? product.physio_price : null;

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-background transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5">
      {product.physio_recommended && (
        <div className="flex items-center gap-1.5 border-b border-primary/15 bg-primary/5 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
          <BadgeCheck className="h-3.5 w-3.5 shrink-0" /> Physio Recommended
        </div>
      )}
      <div className="relative overflow-hidden">
        <Link to="/shop/$slug" params={{ slug: product.slug }} aria-label={product.name}>
          <ProductImage
            src={product.images?.[0]}
            alt={product.name}
            className="h-44 w-full bg-secondary/30 p-4 transition-transform duration-300 group-hover:scale-105 sm:h-52"
          />
        </Link>
        {product.on_sale && product.sale_price != null && (
          <span className="absolute left-3 top-3 rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary-foreground">
            Sale
          </span>
        )}
        <div className="absolute right-1 top-1 flex flex-col">
          <button
            type="button"
            aria-label={wishlisted ? "Remove from wishlist" : "Add to wishlist"}
            aria-pressed={!!wishlisted}
            onClick={() => onWishlist?.(product.id)}
            className="group flex h-11 w-11 items-center justify-center"
          >
            <span className="rounded-full border border-border bg-background/90 p-2 text-muted-foreground group-hover:text-primary">
              <Heart className={`h-4 w-4 ${wishlisted ? "fill-primary text-primary" : ""}`} />
            </span>
          </button>
          <button
            type="button"
            aria-label={compared ? "Remove from compare" : "Add to compare"}
            aria-pressed={!!compared}
            onClick={() => onCompare?.(product.id)}
            className="group flex h-11 w-11 items-center justify-center"
          >
            <span className={`rounded-full border border-border bg-background/90 p-2 group-hover:text-primary ${compared ? "text-primary" : "text-muted-foreground"}`}>
              <Repeat2 className="h-4 w-4" />
            </span>
          </button>
        </div>
      </div>

      <div className="flex flex-1 flex-col p-4">
        {product.brand && (
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{product.brand}</div>
        )}
        <h3 className="mt-1 font-display text-sm font-semibold text-primary">
          <Link to="/shop/$slug" params={{ slug: product.slug }} className="hover:underline underline-offset-2">
            {product.name}
          </Link>
        </h3>
        {product.short_description && (
          <p className="mt-1.5 line-clamp-2 text-xs text-muted-foreground">{product.short_description}</p>
        )}


        <div className="mt-3 space-y-1">
          <div className="flex items-baseline gap-2">
            <span className="font-display text-base font-bold text-primary">
              {formatPrice(standardDisplayPrice(product))}
            </span>
            {product.on_sale && product.sale_price != null && (
              <span className="text-xs text-muted-foreground line-through">{formatPrice(product.standard_price)}</span>
            )}
            <span className="text-[11px] text-muted-foreground">Standard</span>
          </div>
          {physio != null && (
            <div className="flex items-baseline gap-2">
              <Star className="h-3 w-3 shrink-0 fill-primary text-primary" />
              <span className="font-display text-sm font-semibold text-primary">{formatPrice(physio)}</span>
              <span className="text-[11px] text-muted-foreground">Physio Guided</span>
            </div>
          )}
        </div>

        <div className={`mt-2 text-[11px] ${stock.ok ? "text-muted-foreground" : "text-destructive"}`}>{stock.label}</div>

        <div className="mt-auto flex gap-2 pt-4">
          <Link
            to="/shop/$slug"
            params={{ slug: product.slug }}
            className="inline-flex h-11 flex-1 items-center justify-center whitespace-nowrap rounded-full border border-border px-2 text-xs font-semibold text-primary hover:bg-accent"
          >
            View
          </Link>
          <button
            type="button"
            disabled={!stock.ok}
            onClick={() => onAdd(product)}
            className="btn-hero inline-flex h-11 flex-[1.4] items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-2 text-xs font-semibold disabled:opacity-50"
          >
            <ShoppingCart className="h-3.5 w-3.5 shrink-0" /> Add to Cart
          </button>
        </div>
      </div>

    </article>
  );
}
